/**
 * Composite configurable products ("Konfigurovatelné produkty", Type 1).
 *
 * A composite product is a *virtual* product (no Vision SKU of its own, code
 * `cfg_<id>`) composed of categories. Each category holds options that reference
 * existing Vision products by `id_material`. The customer picks at most one option
 * per category; every selected option becomes its own order line item.
 *
 * Newly, not every combination of options is buildable. The admin authors a
 * *pairwise* compatibility matrix (e.g. handle×door, base×door). Pairs the admin
 * never constrains are all-compatible by default. Constraints stay pairwise.
 *
 * Only admin-authored data is persisted; Vision-owned data (name, stock, price)
 * is resolved live at feed-generation time. Detail-page addons (Type 3) are NOT
 * stored here — they live in the separate mapping file (see configurator-mapping.dto).
 */

export enum ConfiguratorChannel {
  B2C = 'b2c',
  B2B = 'b2b',
}

export type ConfiguratorCountry = 'cz' | 'sk'

/**
 * A suggested extra ("doplněk") — one or more units of a real product offered
 * alongside a host on the eshop. Always customer-initiated. Used at three places:
 * a composite product (whole virtual product), a composite option (a specific
 * chosen part), and a variant group (shown on any member's detail).
 */
export interface AddonItemDTO {
  /** Vision id_material of the extra product. */
  sku: string
  /** Default quantity added when the customer picks the extra (e.g. 5). */
  quantity: number
  /** Optional label override; falls back to the real product's name on the eshop. */
  label?: string
  labelSk?: string
  /**
   * Mandatory extra — always added with its host and the customer cannot remove it
   * (e.g. a power cable without which the sauna cannot be connected). Absent = false.
   */
  required?: boolean
}

/** One selectable option inside a category — references an existing Vision product. */
export interface ConfiguratorOptionDTO {
  /** Stable id (uuid) used by `defaultOptionId` and the compatibility matrix. */
  id: string
  /** Vision id_material of the underlying product. Unique within its category. */
  id_material: string
  /**
   * Units of the product one selection puts in the cart (e.g. 1 stove, 250 bm of
   * wood). Absent on older records = 1.
   */
  quantity?: number
  /** Localized variant label shown on the eshop (e.g. "Dub", "Černá 9 kW"). */
  label: string
  labelSk?: string
  /** Custom unit price override; falls back to live Vision price when null/undefined. */
  priceCzk?: number | null
  priceEur?: number | null
  /** Suggested extras shown when this specific option/part is chosen. */
  addons?: AddonItemDTO[]
}

/** A category of the virtual product; the customer picks at most one option. */
export interface ConfiguratorCategoryDTO {
  /** Stable id (uuid). */
  id: string
  label: string
  labelSk?: string
  /** Must the customer pick an option from this category? */
  required: boolean
  /** Pre-selected option id — the base/no-surcharge choice and the diff baseline. */
  defaultOptionId?: string | null
  options: ConfiguratorOptionDTO[]
}

/**
 * Pairwise compatibility between the options of two categories. Presence of a
 * pair means it is *constrained*: only the listed `allowedPairs` may be combined.
 * Absence of a (A,B) pair entirely means the two categories are unconstrained
 * (every option of A is compatible with every option of B).
 */
export interface ConfiguratorCompatibilityPairDTO {
  categoryAId: string
  categoryBId: string
  /** Allowed option combinations across the two categories. */
  allowedPairs: { aOptionId: string; bOptionId: string }[]
}

/** The composite (virtual) product persisted in Mongo. */
export interface ConfigurableProductDTO {
  _id?: string
  /** Generated catalog code, e.g. "cfg_ab12cd34". Assigned by the server. */
  code?: string
  name: string
  nameSk?: string
  description?: string
  descriptionSk?: string
  images: string[]
  /** Which sales channels this config appears in. */
  channels: ConfiguratorChannel[]
  categories: ConfiguratorCategoryDTO[]
  /** Pairwise "what fits with what" constraints; empty/absent = all compatible. */
  compatibility?: ConfiguratorCompatibilityPairDTO[]
  /** Suggested extras shown on the whole virtual product's detail page. */
  addons?: AddonItemDTO[]
  createdAt?: string | Date
  updatedAt?: string | Date
}

/* -------------------------------------------------------------------------- */
/* Product-feed serialization contract (synchronizer XML output → eshop)       */
/* -------------------------------------------------------------------------- */

/** Vision data resolved live at feed-generation time, keyed by id_material. */
export interface ResolvedVisionProduct {
  id_material: string
  name?: string
  /** Available stock (sum of the relevant warehouses). */
  stock: number
  priceCzk?: number
  priceEur?: number
  images?: string[]
}

export interface ConfiguratorFeedOption {
  /** Stable option id — mirrored into the feed so the mapping file's compatibility
   * entries can reference options (the XML otherwise carries only the sku). */
  id: string
  sku: string
  label: string
  /** Units added to the cart when this option is chosen. */
  quantity: number
  /** Absolute effective *unit* price in the feed's currency (override ?? Vision).
   * The option's cart line costs `price × quantity`. */
  price: number | null
  stock: number
  /** Stock covers the whole `quantity`. */
  available: boolean
  isDefault: boolean
}

export interface ConfiguratorFeedCategory {
  id: string
  label: string
  required: boolean
  defaultOptionId?: string | null
  options: ConfiguratorFeedOption[]
}

export interface ConfiguratorFeedProduct {
  code: string
  name: string
  description: string
  images: string[]
  categories: ConfiguratorFeedCategory[]
}

/** Result of validating a configurable product before persisting. */
export interface ConfiguratorValidationError {
  path: string
  message: string
}
